import { useEffect, useRef } from "react";
import { useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm, type SubmitHandler } from "react-hook-form";

import { ColorPickerField } from "@/components/color-picker-field";
import { FieldGroupSurface } from "@/components/field-group-surface";
import { SaveStateInline } from "@/components/save-state-inline";
import { SectionBlock } from "@/components/section-block";
import { Button } from "@/components/ui/button";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  UPDATE_DRAFT_PREVIEW_DESIGN,
  type DraftPreview,
  type UpdateDraftPreviewDesignData,
  type UpdateDraftPreviewDesignVariables,
} from "@/graphql";
import { useAutoSave } from "@/hooks/use-auto-save";
import type { UseAutoSaveState } from "@/hooks/use-auto-save";
import {
  DESIGN_PRESETS,
  designSchema,
  type DesignFormInput,
  type DesignFormValues,
} from "@/pages/design/design-schema";

interface DesignFormProps {
  instagramUsername: string;
  draftPreview: DraftPreview;
  onAutoSaveStateChange?: (state: UseAutoSaveState) => void;
}

const DEFAULT_STYLE: DesignFormValues["style"] = DESIGN_PRESETS[0].style;

export function DesignForm({
  instagramUsername,
  draftPreview,
  onAutoSaveStateChange,
}: DesignFormProps) {
  const form = useForm<DesignFormInput, unknown, DesignFormValues>({
    resolver: zodResolver(designSchema),
    defaultValues: toFormValues(draftPreview),
    mode: "onChange",
  });
  const hydratedUpdatedAtRef = useRef(draftPreview.updatedAt);

  useEffect(() => {
    if (hydratedUpdatedAtRef.current === draftPreview.updatedAt) return;
    if (form.formState.isDirty) return;

    hydratedUpdatedAtRef.current = draftPreview.updatedAt;
    form.reset(toFormValues(draftPreview));
  }, [draftPreview, form]);

  const [updateDesign] = useMutation<
    UpdateDraftPreviewDesignData,
    UpdateDraftPreviewDesignVariables
  >(UPDATE_DRAFT_PREVIEW_DESIGN);

  const onSubmit: SubmitHandler<DesignFormValues> = async (values) => {
    await updateDesign({
      variables: {
        instagramUsername,
        css: values.css,
        style: values.style,
      },
    });
  };

  const autoSaveState = useAutoSave<DesignFormInput>({
    form,
    onSave: async (values) => {
      // Resolve via zod so coerced (numeric) values are passed downstream.
      const parsed = designSchema.safeParse(values);
      if (!parsed.success) return false;
      await onSubmit(parsed.data);
      return true;
    },
    delayMs: 500,
  });

  useEffect(() => {
    onAutoSaveStateChange?.(autoSaveState);
  }, [autoSaveState, onAutoSaveStateChange]);

  function applyPreset(presetName: string) {
    const preset = DESIGN_PRESETS.find((p) => p.name === presetName);
    if (!preset) return;
    form.setValue("style", preset.style, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }

  const { isSubmitting, isValid } = form.formState;

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-10">
        <SectionBlock
          title="Presets"
          description="Start from a preset, then fine-tune the colours and button styling below."
        >
          <FieldGroupSurface className="flex flex-wrap gap-2">
            {DESIGN_PRESETS.map((preset) => (
              <Button
                key={preset.name}
                type="button"
                variant="outline"
                size="sm"
                onClick={() => applyPreset(preset.name)}
              >
                {preset.name}
              </Button>
            ))}
          </FieldGroupSurface>
        </SectionBlock>

        <SectionBlock
          title="General Style"
          description="Set the overall page background and text colour before adjusting individual elements."
        >
          <FieldGroupSurface className="space-y-4">
            <ColorPickerField<DesignFormValues>
              name="style.background"
              label="Background Colour"
              description="The background colour of the page"
            />
            <ColorPickerField<DesignFormValues>
              name="style.fontColour"
              label="Font Colour"
              description="The colour of the text"
            />
          </FieldGroupSurface>
        </SectionBlock>

        <SectionBlock
          title="Button Style"
          description="Adjust the shape, contrast, and surface styling of your link buttons."
        >
          <FieldGroupSurface className="space-y-4">
            <FormField
              control={form.control}
              name="style.buttonRadius"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Button Radius</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="0"
                      name={field.name}
                      ref={field.ref}
                      onBlur={field.onBlur}
                      value={(field.value as number | string | undefined) ?? ""}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  </FormControl>
                  <FormDescription>
                    The amount of curve on the button corners.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="style.buttonBorder"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Border Width</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="0"
                      name={field.name}
                      ref={field.ref}
                      onBlur={field.onBlur}
                      value={(field.value as number | string | undefined) ?? ""}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  </FormControl>
                  <FormDescription>The width of the border.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="style.buttonOpacity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Opacity</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="100"
                      name={field.name}
                      ref={field.ref}
                      onBlur={field.onBlur}
                      value={(field.value as number | string | undefined) ?? ""}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  </FormControl>
                  <FormDescription>
                    How visible your button is, from 0% to 100%.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <ColorPickerField<DesignFormValues>
              name="style.buttonBorderColour"
              label="Border Colour"
              description="The colour of the border"
            />
            <ColorPickerField<DesignFormValues>
              name="style.buttonFontColour"
              label="Font Colour"
              description="The colour of the button text"
            />
            <ColorPickerField<DesignFormValues>
              name="style.buttonBackground"
              label="Background Colour"
              description="The colour of the background of the buttons"
            />
            <ColorPickerField<DesignFormValues>
              name="style.buttonBoxShadowColour"
              label="Box Shadow Colour"
              description="The colour of the box shadow around the button"
            />
          </FieldGroupSurface>
        </SectionBlock>

        <SectionBlock
          title="Custom CSS"
          description="Use custom CSS only when the standard controls are not enough. Changes apply directly to the preview."
        >
          <FieldGroupSurface className="space-y-4 border-blue-100 bg-blue-25/40">
            <FormField
              control={form.control}
              name="css"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Custom CSS</FormLabel>
                  <FormControl>
                    <Textarea
                      rows={8}
                      placeholder=".container { ... }"
                      {...field}
                      value={field.value ?? ""}
                    />
                  </FormControl>
                  <FormDescription>
                    For advanced users. Customise the design further using CSS.
                    More info{" "}
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href="https://help.skedsocial.com/using-advanced-css-with-sked-link-sked-social-help-center?utm_source=skedlink&utm_medium=css_support"
                      className="font-medium text-blue-600 underline"
                    >
                      here
                    </a>
                    .
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FieldGroupSurface>
        </SectionBlock>

        <SaveStateInline
          isSubmitting={isSubmitting}
          isValid={isValid}
          isDirty={autoSaveState.hasUnsavedChanges}
          isAutoSaving={autoSaveState.isSaving}
          isAutoSavePending={autoSaveState.isPending}
          autoSaveErrorMessage={autoSaveState.errorMessage}
        />
      </form>
    </FormProvider>
  );
}

function toFormValues(preview: DraftPreview): DesignFormValues {
  const style = preview.style;
  return {
    css: preview.css ?? "",
    style: {
      buttonRadius: style?.buttonRadius ?? DEFAULT_STYLE.buttonRadius,
      buttonBorder: style?.buttonBorder ?? DEFAULT_STYLE.buttonBorder,
      buttonOpacity: style?.buttonOpacity ?? DEFAULT_STYLE.buttonOpacity,
      buttonBorderColour:
        style?.buttonBorderColour ?? DEFAULT_STYLE.buttonBorderColour,
      buttonFontColour:
        style?.buttonFontColour ?? DEFAULT_STYLE.buttonFontColour,
      buttonBackground:
        style?.buttonBackground ?? DEFAULT_STYLE.buttonBackground,
      buttonBoxShadowColour:
        style?.buttonBoxShadowColour ?? DEFAULT_STYLE.buttonBoxShadowColour,
      fontColour: style?.fontColour ?? DEFAULT_STYLE.fontColour,
      background: style?.background ?? DEFAULT_STYLE.background,
    },
  };
}
